import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Chip, Icon } from "@sagot/ui";
import { useState } from "react";
import { usePermissions } from "../../../session/permissions";
import { useWorkspaceContext } from "../../../session/workspace-context";
import type { LeadDetail } from "../../engage/leads/lead-types";
import { useServiceAreaAccess } from "../use-service-area";
import { LeadConvertDialog } from "./lead-convert-dialog";
import { conversionOf } from "./lead-convert-model";
import { useConvertedOrganisation } from "./use-lead-conversion";

/** Where the lead went: a link to the organisation it became, read from the lead's own timeline. */
function ConvertedLink({ organisationId }: { organisationId: string }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const allowed = usePermissions().can("organisations.view", { anywhere: true });
  const organisation = useConvertedOrganisation(organisationId, allowed);
  if (!allowed) return <Chip tone="success" icon="check-circle" data-testid="lead-converted">{t("service.leadConvert.converted")}</Chip>;
  return (
    <Button asChild size="sm" variant="ghost">
      <AppLink href={consoleRoutes.organisations(summary.slug, organisationId)} data-testid="lead-converted">
        <Icon name="building-2" />
        {organisation.data ? t("service.leadConvert.convertedTo", { name: organisation.data.name }) : t("service.leadConvert.converted")}
      </AppLink>
    </Button>
  );
}

/**
 * "Convert to organisation" on the lead sheet. It needs both `leads.edit` and `organisations.manage` (the API checks both) and the
 * Service organisations page for the workspace (module on, plan includes it). A lead that was converted shows where it went instead.
 * The dialog is kept apart from the button: converting makes the lead read as converted at once, and the dialog still has its result to show.
 */
export function LeadConvertAction({ lead }: { lead: LeadDetail }) {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const access = useServiceAreaAccess("organisations");
  const [open, setOpen] = useState(false);
  const converted = conversionOf(lead.activities);
  const allowed = access.enabled && !access.locked && permissions.can("organisations.manage") && permissions.can("leads.edit") && !permissions.readOnly;
  return (
    <>
      {converted ? <ConvertedLink organisationId={converted.organisationId} /> : null}
      {!converted && allowed ? (
        <Button size="sm" variant="secondary" onClick={() => { setOpen(true); }} data-testid="lead-convert-open">
          <Icon name="building-2" />
          {t("service.leadConvert.actions.open")}
        </Button>
      ) : null}
      {open ? <LeadConvertDialog lead={lead} onClose={() => { setOpen(false); }} /> : null}
    </>
  );
}
